import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { Form, Formik, FormikHelpers, FormikValues } from 'formik';

import { Button, Step, StepLabel, Stepper, StepperProps, useMediaQuery } from '@material-ui/core';
import MobileStepper from '@material-ui/core/MobileStepper';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import { BREAKPOINTS } from 'src/utils/constants';

interface IStyledProps {
  isMobile?: boolean;
  loading?: boolean;
}
const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const StyledStepper = styled(Stepper)<IStyledProps & StepperProps>`
  padding: ${({ isMobile }) => (isMobile && '20px 0px 0px 0px') || 'inherit'};
`;
const StyledStep = styled(Step)`
  .MuiPaper-root {
    background-color: transparent;
  }
  .MuiStepLabel-label {
    font-size: 13px !important;
  }
  .MuiStepIcon-text {
    font-size: 13px !important;
  }
  .MuiStepIcon-completed,
  .MuiStepIcon-active {
    color: ${({ theme }) => theme.colors.primary} !important;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  margin-top: 5%;
  justify-content: space-around;
`;
const StyledButton = styled(Button)`
  font-size: 13px !important;
`;

type TProps = {
  className?: string;
  StepsComp: any[];
  ComponentProps?: {};
  isWantedAd?: boolean;
  SubmitHandler: (val: {
    isLastStep: boolean;
    activeStep: number;
    handleNext: () => void;
    values: FormikValues;
  }) => void;
  initlValues?: any;
};

const Steps: React.FunctionComponent<TProps> = ({
  StepsComp,
  ComponentProps,
  SubmitHandler,
  className,
  initlValues,
  isWantedAd,
}) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const isMobile = useMediaQuery<string>(BREAKPOINTS.MOBILE);

  const isLastStep = (): boolean => {
    return activeStep === StepsComp.length - 1;
  };

  const handlePrev = (): void => {
    setActiveStep(Math.max(activeStep - 1, 0));
  };

  const handleNext = useCallback(() => {
    return [setActiveStep(Math.min(activeStep + 1, StepsComp.length - 1))];
  }, [activeStep, StepsComp.length]);

  const onSubmit = async (values: FormikValues, formikHelpers: FormikHelpers<any>) => {
    formikHelpers.setSubmitting(true);
    await SubmitHandler({
      isLastStep: isLastStep(),
      activeStep,
      handleNext,
      values,
    });
    formikHelpers.setSubmitting(false);
  };

  // Validation
  const ActiveStep = StepsComp[activeStep];
  const { validationSchema } = ActiveStep;
  const validation = ActiveStep.validate;

  const initValues = StepsComp.reduce((values, { initialValues }) => {
    return {
      ...values,
      ...initlValues,
      ...initialValues,
    };
  }, {});

  return (
    <Formik
      validateOnBlur={false}
      initialValues={initValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validate={validation}
    >
      {({ values, isSubmitting }) => (
        <StyledForm className={className}>
          <StyledStepper isMobile={isMobile} alternativeLabel activeStep={activeStep}>
            {StepsComp.map((step: any, index: number) => (
              <StyledStep key={index}>
                <StepLabel>{StepsComp[index].label}</StepLabel>
              </StyledStep>
            ))}
          </StyledStepper>
          {StepsComp.map((step: any, index: number) => {
            const Component = StepsComp[index];
            return (
              activeStep === index && (
                <Component
                  key={index}
                  stepLoading={isSubmitting}
                  handleNext={handleNext}
                  values={values}
                  componentProps={ComponentProps}
                  isWantedAd={isWantedAd}
                  {...ComponentProps}
                />
              )
            );
          })}
          {!isMobile && (
            <ButtonContainer>
              <StyledButton disabled={activeStep === 0 || isSubmitting} onClick={handlePrev}>
                Previous
              </StyledButton>
              <StyledButton disabled={isSubmitting} type="submit">
                {isLastStep() ? 'Finish' : 'Next'}
              </StyledButton>
            </ButtonContainer>
          )}
          {isMobile && (
            <MobileStepper
              variant="dots"
              steps={3}
              position="static"
              activeStep={activeStep}
              nextButton={
                <Button size="small" disabled={isSubmitting} type="submit">
                  {isLastStep() ? 'Finish' : 'Next'}
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button
                  size="small"
                  onClick={handlePrev}
                  disabled={activeStep === 0 || isSubmitting}
                >
                  <KeyboardArrowLeft />
                  Back
                </Button>
              }
            />
          )}
        </StyledForm>
      )}
    </Formik>
  );
};

export default Steps;
